// Credit tier config — single source of truth for both Search and Map tabs.
// Tier 1 (1-2 credits): Products, Gas & EV
// Tier 2 (3 credits): Restaurants, Deals, Rentals, Hotels
// Tier 3 (5 credits): Flights & Multi-Passenger Tickets / Theme Parks
export const TIER_CONFIG = {
  products: { tier: 1, cost: 1, tab: "search" },
  gasev: { tier: 1, cost: 2, tab: "map" },
  deals: { tier: 2, cost: 3, tab: "search" },
  restaurants: { tier: 2, cost: 3, tab: "map" },
  rentalcars: { tier: 2, cost: 3, tab: "map" },
  hotels: { tier: 2, cost: 3, tab: "map" },
  parking: { tier: 1, cost: 2, tab: "map" },
  themeparks: { tier: 3, cost: 5, tab: "search" },
  tickets: { tier: 3, cost: 5, tab: "search" },
  flights: { tier: 3, cost: 5, tab: "map" },
  analyze: { tier: 3, cost: 5, tab: "analyze" },
};

export const tierFor = (mode) => TIER_CONFIG[mode] || { tier: 1, cost: 1, tab: "search" };

// Known direct platforms / major retail & booking sites -> Official/Reseller.
const KNOWN_PLATFORMS = [
  "amazon", "best buy", "bestbuy", "target", "walmart", "ticketmaster", "six flags",
  "sixflags", "ebay", "costco", "seatgeek", "stubhub", "expedia", "kayak", "priceline",
  "marriott", "hilton", "hyatt", "delta", "united", "american airlines", "southwest",
  "hertz", "avis", "enterprise", "budget", "shell", "chevron", "bp", "exxon", "tesla",
  "electrify america", "chargepoint", "apple", "newegg", "google", "booking.com", "booking",
];

export function isKnownPlatform(name) {
  const n = (name || "").toLowerCase();
  return KNOWN_PLATFORMS.some((k) => n.includes(k));
}

// Verification badges for live results.
// Known platform -> Official/Reseller. Solid rating + enough reviews -> Verified.
// Otherwise (no rating, very low rating, or too few reviews) -> Unverified/Warning.
export const VERIFIED_BADGE = { key: "verified", text: "✓ Verified · Trusted", cls: "bg-emerald-600 text-white" };
export const WARNING_BADGE = { key: "unverified", text: "⚠️ Unverified / Warning", cls: "bg-red-600 text-white" };

const VERIFIED_MIN_RATING = 3.5;
const VERIFIED_MIN_REVIEWS = 25;

export function verifyBadge(name, ratingCount = 0, rating = null) {
  if (isKnownPlatform(name)) {
    return { key: "official", text: "🏷️ Official/Reseller", cls: "bg-[#FFD400] text-black" };
  }
  if (rating != null && rating >= VERIFIED_MIN_RATING && ratingCount >= VERIFIED_MIN_REVIEWS) {
    return VERIFIED_BADGE;
  }
  return WARNING_BADGE;
}

export const OWNER_BADGE = {
  key: "owner",
  text: "👑 Owner Sponsored / Official",
  cls: "bg-black text-[#FFD400]",
};

// Owner brand (Southwest) gets pinned + bypasses truncation.
export function isOwnerSponsored(...vals) {
  return vals.some((v) => (v || "").toLowerCase().includes("southwest"));
}

// Final badge: owner > verification.
export function resultBadge(name, ratingCount, owner, rating = null) {
  if (owner) return OWNER_BADGE;
  return verifyBadge(name, ratingCount, rating);
}

// Clean, never-blank provider/merchant name from any available field or URL.
export function cleanSiteName(it = {}) {
  if (it.source) return it.source;
  if (it.merchant) return it.merchant;
  if (it.domain) return it.domain;
  const link = it.link || it.storeLink || it.website || it.url || "";
  try {
    const host = new URL(link).hostname.replace(/^www\./, "");
    if (host) return host;
  } catch {
    /* not a URL */
  }
  return it.title || "Unknown";
}

// First dollar-currency pattern in any text (e.g. "$45", "$120/ea", "$3.45/gal").
const PRICE_RE = /\$\s?\d[\d,]*(?:\.\d{1,2})?(?:\s?\/\s?\w+)?/i;
export function extractPrice(...texts) {
  for (const t of texts) {
    if (!t) continue;
    const m = String(t).match(PRICE_RE);
    if (m) return m[0].replace(/\s+/g, "");
  }
  return "";
}

export const PRICE_FALLBACK = "Click to View Current Rates";
